/**
 * RecordDocList — renders system-generated documents (issued invoices / vendor
 * bills) as read-only rows in the shared `MasterListCard`. Each row downloads
 * the server-rendered PDF via the existing `/api/<doc>/:id/pdf` route. These
 * are not editable here — they're projections of their source records.
 */
import { Receipt, ShoppingCart, Download, ExternalLink } from "lucide-react";
import { cn, StatusPill, type StatusTone } from "@flint/ui";
import { MasterListCard, railClass } from "../../master-list/index.ts";
import { openDocPdf } from "../../../lib/pdf.ts";
import { formatDate, paiseToInrShort } from "../../../lib/format.ts";

export interface RecordDoc {
  id: string;
  kind: "invoice" | "bill";
  title: string;
  party: string;
  date: string;
  amountPaise: string;
  status: string;
  statusLabel: string;
  statusTone: StatusTone;
}

export function RecordDocList({ items, footerLabel }: { items: RecordDoc[]; footerLabel: string }) {
  return (
    <MasterListCard
      header={
        <div className="grid grid-cols-[44px_minmax(0,1.6fr)_120px_110px_120px_auto] items-center gap-4 px-5 py-2.5">
          <span aria-hidden />
          <span>Document</span>
          <span>Status</span>
          <span className="text-right">Date</span>
          <span className="text-right">Amount</span>
          <span aria-hidden />
        </div>
      }
      footer={[{ label: footerLabel, value: items.length }]}
    >
      {items.map((r) => (
        <RecordRow key={r.id} doc={r} />
      ))}
    </MasterListCard>
  );
}

function RecordRow({ doc: r }: { doc: RecordDoc }) {
  const Icon = r.kind === "invoice" ? Receipt : ShoppingCart;
  const pdfPath = r.kind === "invoice" ? `/api/invoices/${r.id}/pdf` : `/api/bills/${r.id}/pdf`;

  return (
    <div
      className={cn(
        "group relative grid items-center gap-3 px-4 py-3.5 transition-colors hover:bg-raised md:gap-4 md:px-5",
        "grid-cols-[44px_minmax(0,1fr)_auto]",
        "md:grid-cols-[44px_minmax(0,1.6fr)_120px_110px_120px_auto]",
      )}
    >
      <span aria-hidden className={railClass("none")} />

      <span
        aria-hidden
        className={cn(
          "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md shadow-sm",
          r.kind === "invoice" ? "bg-sky-50 text-sky-700" : "bg-warn-50 text-warn",
        )}
      >
        <Icon size={19} strokeWidth={2} />
      </span>

      <div className="min-w-0">
        <p className="truncate text-base font-bold tracking-tight text-ink">{r.title}</p>
        <p className="truncate text-xs text-muted">{r.party}</p>
      </div>

      <div className="hidden md:block">
        <StatusPill tone={r.statusTone} size="xs" dot>
          {r.statusLabel}
        </StatusPill>
      </div>

      <div className="hidden text-right md:block">
        <span className="text-xs tabular-nums text-muted">{formatDate(r.date)}</span>
      </div>

      <div className="hidden text-right md:block">
        <span className="text-sm font-semibold tabular-nums text-ink">{paiseToInrShort(r.amountPaise)}</span>
      </div>

      <div className="flex items-center justify-end gap-1">
        <button
          type="button"
          onClick={() => openDocPdf(pdfPath)}
          title="Open"
          className="inline-flex h-8 w-8 items-center justify-center rounded-sm border border-line bg-paper text-muted hover:bg-bg hover:text-ink"
        >
          <ExternalLink size={14} aria-hidden />
        </button>
        <button
          type="button"
          onClick={() => openDocPdf(pdfPath, { download: true, filename: r.title.replace(/[^\w.-]+/g, "-") })}
          title="Download PDF"
          className="inline-flex h-8 w-8 items-center justify-center rounded-sm border border-line bg-paper text-muted hover:bg-bg hover:text-ink"
        >
          <Download size={14} aria-hidden />
        </button>
      </div>
    </div>
  );
}
